import { Box, Stack, SxProps, Typography, TextField } from '@mui/material';
import React from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { currencyUtils } from 'skipify-types';

import { formatCurrency } from '../../../../util/formatters';
import { AcceptedCurrency } from '../../../../store/modules/merchant/types';

type Props<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  disabled: boolean;
  errorMessage?: string;
  refundableAmount: number;
  value: string;
  sx?: SxProps;
  currency?: AcceptedCurrency;
};

export const RefundInput = <TFieldValues extends FieldValues>({
  control,
  name,
  disabled,
  errorMessage,
  refundableAmount,
  value,
  currency,
  sx = {},
}: Props<TFieldValues>) => {
  const currencySymbol = currency?.symbol || '$';
  const displayedRefundableAmount =
    refundableAmount === 0
      ? `${currencySymbol}0`
      : formatCurrency(currencyUtils.minorToMajor(refundableAmount), currency?.code || 'USD');

  return (
    <Stack spacing={1} sx={sx}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <NumericFormat
            disabled={disabled}
            customInput={TextField}
            variant="outlined"
            label="Refund Amount"
            thousandSeparator
            prefix={currencySymbol}
            fixedDecimalScale
            decimalScale={2}
            allowNegative={false}
            error={!!errorMessage}
            helperText={errorMessage}
            onChange={field.onChange}
            onBlur={field.onBlur}
            value={field.value}
            name={field.name}
            id={field.name}
          />
        )}
      />
      <Stack
        sx={{
          '& > div': {
            display: 'flex',
            justifyContent: 'space-between',
          },
          '& p': {
            fontSize: 14,
            color: 'black',
          },
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: '700' }}>Available amount to refund:</Typography>
          <Typography>{displayedRefundableAmount}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontWeight: '700' }}>Refund amount:</Typography>
          <Typography>{value === '' ? `${currencySymbol}0` : value}</Typography>
        </Box>
      </Stack>
    </Stack>
  );
};
